import {
  PassType,
  PassStatus,
  PASSES,
  PASS_ORDER,
  PASS_FEATURES,
  UPGRADE_PRICES,
  EffectivePassInfo,
  COMPLETE_PASS_PDF_KINDS,
} from "../types/pass";
import { parseKstInstant } from "./kst-date";
import { getCurrentPassSeason, PREMIUM_COMPLETE_PASS_SALES_SEASONS } from "./pass-sales";

/** /api/purchases 응답 항목 중 요약 PDF 개별구매 판정에 필요한 최소 필드 */
export interface PurchaseRecord {
  productId?: string;
  status?: string;
  amount?: number;
}

/** 요약 PDF 개별구매(amount>0) 판정 결과 */
export interface OwnedSummaryPdfInfo {
  /** 개별 구매한 요약 PDF productId 집합 */
  productIds: string[];
  /** 개별 구매한 요약 PDF 개수 (1 이상이면 해당 회차 Complete 구매 차단 대상) */
  count: number;
}

/**
 * /api/purchases 구매목록에서 대상 요약 PDF를 개별 구매(status='paid' AND amount>0)한
 * productId 집합과 개수를 반환한다. (JCG-2291)
 *
 * Complete 이용권 구매 시 무료로 제공되는 amount=0 주문(isFreeGift)은 개별 구매가 아니므로 제외된다.
 * 동일 productId가 여러 주문에 있어도 1개로 집계한다.
 *
 * @param targetProductIds 집계 대상 PDF productId 목록. Complete 중복구매 차단 판정에는
 *   구매하려는 Complete 회차의 구성(getCompletePassFreePdfProductIds())을 넘긴다(JCG-3580).
 *   회차를 고정한 상수를 기본값으로 두면 회차가 바뀔 때 조용히 틀리므로 필수 인자로 받는다.
 */
export function getOwnedSummaryPdfs(
  purchases: readonly PurchaseRecord[] | null | undefined,
  targetProductIds: readonly string[]
): OwnedSummaryPdfInfo {
  if (!purchases || purchases.length === 0) {
    return { productIds: [], count: 0 };
  }

  const targetIds: readonly string[] = targetProductIds;
  const owned = new Set<string>();

  for (const purchase of purchases) {
    if (!purchase) continue;
    const { productId, status, amount } = purchase;
    if (!productId || !targetIds.includes(productId)) continue;
    if (status !== 'paid') continue;
    // 개별 구매만 집계: Complete 무료 제공(amount=0)은 제외
    if (typeof amount !== 'number' || amount <= 0) continue;
    owned.add(productId);
  }

  const productIds = [...owned];
  return { productIds, count: productIds.length };
}

/**
 * 요약 PDF productId를 종류 라벨(이론/코딩)로 변환한다. 안내 문구용. (JCG-2289/2291)
 * 회차와 무관하게 COMPLETE_PASS_PDF_KINDS 매핑을 따른다(JCG-3580).
 */
export function getSummaryPdfKindLabel(productId: string): string {
  const kind = COMPLETE_PASS_PDF_KINDS[productId];
  if (kind === 'theory') return '이론';
  if (kind === 'coding') return '코딩';
  return '';
}

/**
 * 개별구매한 요약 PDF productId 목록을 "이론·코딩" 형태의 종류 라벨로 합친다(안내 문구용).
 * 예: ['26-3-compressed-pdf'] → "이론", 이론·코딩 둘 다면 → "이론·코딩". 빈 목록이면 "".
 */
export function formatOwnedSummaryPdfKinds(productIds: readonly string[]): string {
  // 표시 순서 고정(이론 → 코딩) — 구매 순서(paidAt)에 의존하지 않도록 종류 기준으로 정렬.
  const kinds = new Set(productIds.map(getSummaryPdfKindLabel).filter(Boolean));
  return ['이론', '코딩'].filter((label) => kinds.has(label)).join('·');
}

// ============================================================
// Complete 결제액 → 요약 PDF 1개분 부분환불 단가 매핑 (JCG-2373)
// ============================================================
// Complete 결제액(orders.amount)으로부터 youtube/정가 tier를 역판별해
// 중복구매(개별 요약 PDF) 부분환불 단가를 자동 산정한다.
//
// 각 tier의 "Complete 결제액 집합"은 풀가(PASSES.complete)와
// 업그레이드 차액(UPGRADE_PRICES 중 to:'complete' 행)에서 파생한다:
//   - youtube tier: { 25000(풀가), 23000(starter→), 12000(premium→) } → 단가 6000
//   - 정가 tier:    { 30000(풀가), 27000(starter→), 15000(premium→) } → 단가 7500
// 두 집합은 교집합이 없어 금액→tier가 유일하다.
// (반드시 to:'complete' 행만 사용 — premium→intelligence youtubePrice 23000 등
//  다른 to 행을 포함하면 23000이 두 tier로 모호해진다.)
//
const COMPLETE_UPGRADE_PRICES = UPGRADE_PRICES.filter((up) => up.to === 'complete');
const COMPLETE_YOUTUBE_AMOUNTS = new Set<number>([
  PASSES.complete.youtubePrice,
  ...COMPLETE_UPGRADE_PRICES.map((up) => up.youtubePrice),
]);
const COMPLETE_FULL_PRICE_AMOUNTS = new Set<number>([
  PASSES.complete.price,
  ...COMPLETE_UPGRADE_PRICES.map((up) => up.price),
]);

/**
 * Complete 결제액으로 youtube/정가 tier를 역판별해 tier별 고정 단가를 반환하는 공통 lookup.
 * 풀가(25000/30000)뿐 아니라 업그레이드 차액(youtube 23000/12000, 정가 27000/15000)도 매핑된다.
 * 매핑 외 금액이면 0(수동 확정 필요).
 */
function completePdfUnitPriceFor(
  completeAmount: number,
  youtubeTierPrice: number,
  fullPriceTierPrice: number
): number {
  if (COMPLETE_YOUTUBE_AMOUNTS.has(completeAmount)) return youtubeTierPrice;
  if (COMPLETE_FULL_PRICE_AMOUNTS.has(completeAmount)) return fullPriceTierPrice;
  return 0;
}

/**
 * Complete 이용권에 포함된 PDF를 이미 다운로드한 경우의 PDF 1개분 차감 단가.
 *
 * Complete 이용권 약관상 포함 PDF 가치 기준(8,000/10,000원)이다.
 */
export function getCompleteIncludedPdfRefundUnit(completeAmount: number): number {
  return completePdfUnitPriceFor(completeAmount, 8000, 10000);
}

/**
 * Complete 이용권에 포함된 PDF가 다운로드되지 않은 경우 보전할 PDF 1개분 할인가.
 *
 * 약관상 Complete 환불 산식에서 프리미엄 기능분과 PDF 할인분을 분리할 때 사용한다.
 * 다운로드된 PDF 차감은 정가/특가(8,000/10,000원), 미다운로드 PDF 보전은
 * Complete 구성상 할인분(7,000/9,000원)으로 계산한다.
 */
export function getCompleteIncludedPdfDiscountUnit(completeAmount: number): number {
  return completePdfUnitPriceFor(completeAmount, 7000, 9000);
}

/**
 * Nicepay 주문 ID 생성 헬퍼 (구매 모달 공통)
 */
export function generateOrderId(prefix: string): string {
  const randomId = globalThis.crypto.randomUUID().replaceAll("-", "");
  return `${prefix}${randomId}`;
}

/**
 * 이용권 활성화 여부 확인
 */
export function isPassActive(passType: PassType, expiresAt: string | null): boolean {
  // free는 항상 활성화
  if (passType === 'free') return true;

  // starter는 평생 (expiresAt이 null)
  if (passType === 'starter' && expiresAt === null) return true;

  // 만료일이 있는 경우 현재 시간과 비교
  if (expiresAt) {
    return new Date(expiresAt) > new Date();
  }

  return false;
}

/**
 * 이용권별 만료일 반환
 * starter: null (평생)
 * 그 외: 현재 판매 시즌의 만료일
 */
export function getPassExpiryDate(
  passType: PassType,
  now: number | string | Date = Date.now()
): string | null {
  if (passType === 'starter') return null;
  return getCurrentSeasonExpiryDate(now);
}

/**
 * 현재 판매 시즌의 Premium·Complete 만료일.
 *
 * 첫 회차는 salesStartAtISO 가 null 이라 getCurrentPassSeason 은 null 을 돌려주지 않는다.
 * 폴백은 타입 가드용으로, 회차 리터럴을 따로 두지 않고 SSOT 의 첫 회차를 쓴다.
 */
function getCurrentSeasonExpiryDate(now: number | string | Date): string {
  return (getCurrentPassSeason(now) ?? PREMIUM_COMPLETE_PASS_SALES_SEASONS[0]).expiresAtISO;
}

/**
 * 특정 기능 접근 권한 확인
 * @param userPassType 현재 이용권 타입
 * @param featureName 확인할 기능 이름
 * @param starterPurchasedAt Starter 구매일 (있으면 평생 접근 가능)
 */
export function hasFeatureAccess(
  userPassType: PassType,
  featureName: string,
  starterPurchasedAt?: string | null
): boolean {
  // Starter 기능 목록
  const starterFeatures: readonly string[] = [
    PASS_FEATURES.adFree,
    PASS_FEATURES.tableOfContents,
    PASS_FEATURES.noteFeature,
    PASS_FEATURES.pageBookmark,
  ];

  // Starter 기능이고, starterPurchasedAt이 있으면 평생 접근 가능
  if (starterFeatures.includes(featureName) && starterPurchasedAt) {
    return true;
  }

  // 일반 기능 체크
  const userPass = PASSES[userPassType];
  return userPass.features.includes(featureName);
}

/**
 * 사용자의 현재 이용권 상태 확인
 */
export function getPassStatus(
  passType: PassType | undefined,
  expiresAt: string | null | undefined,
  purchasedAt: string | undefined
): PassStatus {
  // 이용권이 없으면 free
  if (!passType) {
    return {
      passType: 'free',
      expiresAt: null,
      isActive: true,
    };
  }

  const isActive = isPassActive(passType, expiresAt ?? null);

  return {
    passType,
    expiresAt: expiresAt ?? null,
    isActive,
    purchasedAt,
  };
}

/**
 * 이용권 등급 비교 (상위 이용권인지 확인)
 * 반환값: 양수면 pass1이 더 높음, 음수면 pass2가 더 높음, 0이면 같음
 */
export function comparePassLevel(pass1: PassType, pass2: PassType): number {
  const index1 = PASS_ORDER.indexOf(pass1);
  const index2 = PASS_ORDER.indexOf(pass2);
  return index1 - index2;
}

/**
 * 업그레이드 필요한 기능인지 확인
 */
export function needsUpgrade(
  currentPassType: PassType,
  requiredFeature: string
): boolean {
  return !hasFeatureAccess(currentPassType, requiredFeature);
}

/**
 * 특정 기능에 접근하기 위해 필요한 최소 이용권 타입 반환
 */
export function getRequiredPassForFeature(featureName: string): PassType {
  for (const passType of PASS_ORDER) {
    if (PASSES[passType].features.includes(featureName)) {
      return passType;
    }
  }
  return 'intelligence'; // 가장 높은 등급
}

/**
 * 광고 제거 여부 확인
 * @param passType 현재 이용권 타입
 * @param starterPurchasedAt Starter 구매일 (있으면 평생 접근 가능)
 */
export function isAdFree(passType: PassType, starterPurchasedAt?: string | null): boolean {
  return hasFeatureAccess(passType, PASS_FEATURES.adFree, starterPurchasedAt);
}

/**
 * PDF 다운로드 권한 확인
 */
export function canDownloadPdf(passType: PassType): boolean {
  return hasFeatureAccess(passType, PASS_FEATURES.theoryPdf) ||
         hasFeatureAccess(passType, PASS_FEATURES.codingPdf);
}

/**
 * 이용권 만료일까지 남은 일수 계산
 */
export function getDaysUntilExpiry(expiresAt: string | null): number | null {
  if (!expiresAt) return null; // 평생

  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffTime = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays > 0 ? diffDays : 0;
}

/**
 * 이용권 표시 이름 반환
 */
export function getPassDisplayName(passType: PassType): string {
  return PASSES[passType].name;
}

/**
 * 가격 포맷팅
 */
export function formatPrice(price: number): string {
  return price.toLocaleString('ko-KR') + '원';
}

/**
 * 이미 계산된 현재 판매 시즌 만료일로 이전 시즌 이용권을 판정한다.
 * 현재 시각을 읽지 않는 순수 함수이므로 SSR/프리렌더 경로에서 사용한다.
 */
export function isPreviousSeasonPassForExpiry(
  currentPassType: PassType,
  passExpiresAt: string | null | undefined,
  currentSeasonExpiresAt: string
): boolean {
  if (currentPassType !== 'premium' && currentPassType !== 'complete') return false;
  if (!passExpiresAt) return false;
  return passExpiresAt !== currentSeasonExpiresAt;
}

/**
 * 이전 시즌 이용권 갱신 대상 여부 확인
 * premium/complete 보유자가 이전 시즌(만료일 ≠ 현재 시즌 만료일) 이용권을 보유한 경우 true
 */
export function isPreviousSeasonPass(
  currentPassType: PassType,
  passExpiresAt: string | null | undefined,
  now: number | string | Date = Date.now()
): boolean {
  const currentSeasonExpiresAt = getCurrentSeasonExpiryDate(now);
  return isPreviousSeasonPassForExpiry(
    currentPassType,
    passExpiresAt,
    currentSeasonExpiresAt
  );
}

/**
 * 업그레이드 가격 조회
 * @param currentPassType 현재 보유한 이용권 타입
 * @param targetPassType 구매하려는 이용권 타입
 * @param isYoutubeSubscriber 유튜브 구독자 여부
 * @param currentPassExpiresAt 현재 이용권 만료일 (이전 시즌이면 업그레이드 가격 미적용)
 * @param currentSeasonExpiresAt 이미 계산된 현재 판매 시즌 만료일 (SSR 렌더 시 명시)
 * @returns 업그레이드 가격이 있으면 가격 반환, 없으면 null
 */
export function getUpgradePrice(
  currentPassType: PassType,
  targetPassType: PassType,
  isYoutubeSubscriber: boolean,
  currentPassExpiresAt?: string | null,
  currentSeasonExpiresAt?: string
): number | null {
  // 이전 시즌(1회) 이용권 보유자는 업그레이드 가격 미적용 — 갱신이므로 정가
  const isPreviousSeason = currentSeasonExpiresAt === undefined
    ? isPreviousSeasonPass(currentPassType, currentPassExpiresAt)
    : isPreviousSeasonPassForExpiry(
        currentPassType,
        currentPassExpiresAt,
        currentSeasonExpiresAt
      );
  if (isPreviousSeason) {
    return null;
  }

  // 업그레이드 가격 정보 찾기
  const upgradePrice = UPGRADE_PRICES.find(
    (up) => up.from === currentPassType && up.to === targetPassType
  );

  if (!upgradePrice) {
    return null;
  }

  return isYoutubeSubscriber ? upgradePrice.youtubePrice : upgradePrice.price;
}

/**
 * 타겟 이용권이 현재 보유한 이용권보다 하위인지 확인
 * @param currentPassType 현재 보유한 이용권 타입
 * @param targetPassType 확인하려는 이용권 타입
 * @returns 하위 이용권이면 true, 그렇지 않으면 false
 */
export function isLowerTierPass(
  currentPassType: PassType,
  targetPassType: PassType
): boolean {
  const currentIndex = PASS_ORDER.indexOf(currentPassType);
  const targetIndex = PASS_ORDER.indexOf(targetPassType);

  // target이 current보다 낮은 인덱스면 하위 이용권
  return targetIndex < currentIndex;
}

// ============================================================
// 무료체험 관련 유틸리티
// ============================================================

/**
 * 시험일까지 남은 일수 기반 체험 기간 계산
 * - 60일 이상 → 7일
 * - 30~60일 → 5일
 * - 7~30일 → 3일
 * - 7일 미만 또는 시험일 지남 → 0일 (체험 없음)
 */
export function calculateTrialDuration(now: Date = new Date()): number {
  const season = getCurrentPassSeason(now);
  if (!season) return 0;
  // 공식 시험일 미정인 새 시즌도 기본 체험을 유지한다. 잠정 이용기간이
  // 끝난 뒤에는 지급하지 않으며, 시험일 등록 후에는 아래 기존 정책을 따른다.
  if (!season.examDateISO) return now.getTime() < Date.parse(season.expiresAtISO) ? 7 : 0;

  const examDate = parseKstInstant(season.examDateISO);
  const diffMs = examDate.getTime() - now.getTime();
  const daysUntilExam = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (daysUntilExam >= 60) return 7;
  if (daysUntilExam >= 30) return 5;
  if (daysUntilExam >= 7) return 3;
  return 0; // 체험 없음
}

/**
 * 체험 만료일 ISO 문자열 반환
 * 체험 기간이 0이면 null 반환
 */
export function calculateTrialExpiryDate(now: Date = new Date()): string | null {
  const duration = calculateTrialDuration(now);
  if (duration === 0) return null;

  const expiryDate = new Date(now);
  expiryDate.setDate(expiryDate.getDate() + duration);
  // 만료일 23:59:59 KST (= 14:59:59 UTC)
  expiryDate.setUTCHours(14, 59, 59, 0);
  return expiryDate.toISOString();
}

/**
 * 체험 활성화 여부
 */
export function isTrialActive(freeTrialExpiresAt: string | null | undefined): boolean {
  if (!freeTrialExpiresAt) return false;
  return new Date(freeTrialExpiresAt) > new Date();
}

/**
 * 남은 체험 일수
 */
export function getTrialRemainingDays(freeTrialExpiresAt: string | null | undefined): number {
  if (!freeTrialExpiresAt) return 0;
  const diffMs = new Date(freeTrialExpiresAt).getTime() - new Date().getTime();
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 0;
}

/**
 * 구매 + 체험 메타데이터를 종합하여 실질적 패스 정보 반환
 * 구매 패스가 활성이면 우선, 아니면 체험 확인
 */
export function getEffectivePassType(metadata: {
  passType?: PassType;
  passExpiresAt?: string | null;
  starterPurchasedAt?: string | null;
  freeTrialStartedAt?: string | null;
  freeTrialExpiresAt?: string | null;
  freeTrialTier?: PassType;
}): EffectivePassInfo {
  const purchasedPassType = metadata.passType || 'free';
  const passExpiresAt = metadata.passExpiresAt ?? null;

  // 1. 구매 패스가 활성이면 우선
  if (purchasedPassType !== 'free') {
    const active = isPassActive(purchasedPassType, passExpiresAt);
    if (active) {
      return {
        passType: purchasedPassType,
        isActive: true,
        isTrial: false,
        isTrialExpired: isTrialExpired(metadata.freeTrialExpiresAt),
      };
    }
    // premium 이상 만료 후 starterPurchasedAt이 있으면 starter로 폴백
    if (metadata.starterPurchasedAt) {
      return {
        passType: 'starter',
        isActive: true,
        isTrial: false,
        isTrialExpired: isTrialExpired(metadata.freeTrialExpiresAt),
      };
    }
  }

  // 2. 체험 확인
  if (metadata.freeTrialExpiresAt && metadata.freeTrialTier) {
    const trialActive = isTrialActive(metadata.freeTrialExpiresAt);
    if (trialActive) {
      return {
        passType: metadata.freeTrialTier,
        isActive: true,
        isTrial: true,
        isTrialExpired: false,
      };
    }
    // 체험 만료 → 구매 패스가 활성이면 구매 패스로, 아니면 free (free는 항상 활성)
    const hasPaidPass = purchasedPassType !== 'free' && isPassActive(purchasedPassType, passExpiresAt);
    return {
      passType: hasPaidPass ? purchasedPassType : 'free',
      isActive: true, // 활성 패스로 복귀하거나 free(항상 활성)로 복귀
      isTrial: false,
      isTrialExpired: true,
    };
  }

  // 3. 체험도 구매도 없음
  return {
    passType: 'free',
    isActive: true, // free는 항상 활성
    isTrial: false,
    isTrialExpired: false,
  };
}

/**
 * 체험 만료 여부 (체험이 있었지만 만료된 상태)
 */
function isTrialExpired(freeTrialExpiresAt: string | null | undefined): boolean {
  if (!freeTrialExpiresAt) return false;
  return new Date(freeTrialExpiresAt) <= new Date();
}
